import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/auth/auth-layout";
import { Button } from "@/components/ui/button";
import { FormError } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";
import { useAuthStatus } from "@/lib/use-auth-status";

export const Route = createFileRoute("/invite/$invitationId")({
  component: InvitePage,
});

type Invitation = {
  organizationName: string;
  organizationSlug: string;
  inviterEmail: string;
  role: string;
};

function InvitePage() {
  const status = useAuthStatus();
  const { invitationId } = Route.useParams();
  if (status === "loading") return null;
  return status === "signedIn" ? (
    <AcceptInvitation invitationId={invitationId} />
  ) : (
    <SignInFirst invitationId={invitationId} />
  );
}

/** An invitation is addressed to one email, so we need to know who is asking. */
function SignInFirst({ invitationId }: { invitationId: string }) {
  const { t } = useTranslation("auth");
  const redirect = `/invite/${invitationId}`;
  return (
    <AuthLayout title={t("invite.title")} description={t("invite.signInFirst")}>
      <div className="flex flex-wrap gap-2">
        <Button asChild>
          <Link to="/signup" search={{ redirect }}>
            {t("signup.submit")}
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/login" search={{ redirect }}>
            {t("login.submit")}
          </Link>
        </Button>
      </div>
    </AuthLayout>
  );
}

function AcceptInvitation({ invitationId }: { invitationId: string }) {
  const { t } = useTranslation("auth");
  const { t: tErrors } = useTranslation("errors");
  const { t: tSettings } = useTranslation("settings");
  const navigate = useNavigate();
  const workspaces = useQuery(api.workspaces.listMine);
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState<"accept" | "decline" | null>(null);
  const [joinedSlug, setJoinedSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void authClient.organization
      .getInvitation({ query: { id: invitationId } })
      .then((result) => {
        if (cancelled) return;
        if (result.error || !result.data) {
          setLoadError(authErrorMessage(result.error, tErrors));
        } else if (result.data.status !== "pending") {
          setLoadError(t("invite.used"));
        } else {
          setInvitation(result.data);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [invitationId, t, tErrors]);

  // The membership is mirrored into the app as part of the accept; open the
  // workspace once this client has seen it, not a moment before.
  useEffect(() => {
    if (joinedSlug && workspaces?.some((workspace) => workspace.slug === joinedSlug)) {
      void navigate({ to: "/$workspaceSlug", params: { workspaceSlug: joinedSlug } });
    }
  }, [joinedSlug, workspaces, navigate]);

  if (loadError) {
    return (
      <AuthLayout title={t("invite.unavailable")} description={loadError}>
        <div>
          <Button variant="outline" asChild>
            <Link to="/">{t("invite.openApp")}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (!invitation) {
    return (
      <AuthLayout title={t("invite.title")}>
        <div className="flex flex-col gap-2" aria-hidden>
          <Skeleton className="h-3 w-56" />
          <Skeleton className="h-3 w-40" />
        </div>
      </AuthLayout>
    );
  }

  const role = invitation.role === "admin" || invitation.role === "owner" ? invitation.role : "member";

  async function accept() {
    if (!invitation) return;
    setActionError(null);
    setPending("accept");
    const result = await authClient.organization.acceptInvitation({ invitationId });
    if (result.error) {
      setActionError(authErrorMessage(result.error, tErrors));
      setPending(null);
      return;
    }
    setJoinedSlug(invitation.organizationSlug);
  }

  async function decline() {
    setActionError(null);
    setPending("decline");
    const result = await authClient.organization.rejectInvitation({ invitationId });
    if (result.error) {
      setActionError(authErrorMessage(result.error, tErrors));
      setPending(null);
      return;
    }
    void navigate({ to: "/" });
  }

  return (
    <AuthLayout
      title={t("invite.join", { workspace: invitation.organizationName })}
      description={t("invite.from", {
        inviter: invitation.inviterEmail,
        role: tSettings(`roles.${role}`),
      })}
    >
      <FormError>{actionError}</FormError>
      <div className="flex flex-wrap gap-2">
        <Button disabled={pending !== null} onClick={accept}>
          {pending === "accept" ? <Spinner /> : null}
          {t("invite.accept")}
        </Button>
        <Button variant="ghost" disabled={pending !== null} onClick={decline}>
          {t("invite.decline")}
        </Button>
      </div>
    </AuthLayout>
  );
}
